import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from 'api/v1';

export const PERSONAL_DATA_REQUEST_QUERY_KEY = ['personal-data-change-request', 'mine'];

export type PersonalDataRequestStatus =
  | 'pending_review'
  | 'awaiting_user_decision'
  | 'awaiting_otp'
  | 'approved'
  | 'rejected'
  | 'expired'
  | 'changes_requested';

export interface PersonalDataRequestMine {
  id: number;
  status: PersonalDataRequestStatus;
  reason: string;
  current: Record<string, unknown>;
  proposed: Record<string, unknown>;
  comment?: string | null;
  decision_expires_at?: string | null;
  contact_email_sent_at?: string | null;
}

const STATUS_LABELS: Record<string, string> = {
  pending_review: 'На рассмотрении',
  awaiting_user_decision: 'Ожидает вашего ответа по email',
  awaiting_otp: 'Введите код из письма',
  approved: 'Одобрена',
  rejected: 'Отклонена',
  expired: 'Ссылка истекла - обратитесь в поддержку',
  changes_requested: 'Нужны правки',
};

export const VK_COMMUNITY_URL =
  'https://vk.com/im/convo/-238748758?entrypoint=community_page&tab=all';

export function personalDataStatusLabel(status: string): string {
  return STATUS_LABELS[status] || status;
}

export function useMyPersonalDataRequest() {
  return useQuery({
    queryKey: PERSONAL_DATA_REQUEST_QUERY_KEY,
    queryFn: async () => {
      const { data } = await api.get<PersonalDataRequestMine | null>(
        '/personal-data-change-requests/mine/',
        { skipGlobalErrorHandler: true },
      );
      return data;
    },
  });
}

export function useCreatePersonalDataRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (reason: string) => {
      const { data } = await api.post(
        '/personal-data-change-requests/',
        { reason },
        { skipGlobalErrorHandler: true },
      );
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PERSONAL_DATA_REQUEST_QUERY_KEY });
    },
  });
}

export function usePreviewPersonalDataDecision(token: string | null) {
  return useQuery({
    queryKey: ['personal-data-decision-preview', token],
    queryFn: async () => {
      const { data } = await api.get('/personal-data-change-requests/decision/', {
        params: { token },
        skipAuthRefresh: true,
      });
      return data;
    },
    enabled: Boolean(token),
  });
}

export function useConfirmPersonalDataDecision() {
  return useMutation({
    mutationFn: async (token: string) => {
      const { data } = await api.post(
        '/personal-data-change-requests/decision/confirm/',
        { token },
        { skipAuthRefresh: true },
      );
      return data as { request_id: number; message: string };
    },
  });
}

export function useRejectPersonalDataDecision() {
  return useMutation({
    mutationFn: async (token: string) => {
      const { data } = await api.post(
        '/personal-data-change-requests/decision/reject/',
        { token },
        { skipAuthRefresh: true },
      );
      return data;
    },
  });
}

export function useVerifyPersonalDataOtp() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { request_id: number; code: string; browser_key: string }) => {
      const { data } = await api.post('/personal-data-change-requests/otp/verify/', payload);
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PERSONAL_DATA_REQUEST_QUERY_KEY });
    },
  });
}

export function useResendPersonalDataOtp() {
  return useMutation({
    mutationFn: async (payload: { request_id: number; browser_key: string }) => {
      const { data } = await api.post('/personal-data-change-requests/otp/resend/', payload);
      return data;
    },
  });
}
